import type { ActorRef, Attribution } from "../types";
import { formatDate, formatDateTime } from "../lib/datetime";

const roleLabels: Record<string, string> = {
  super_admin: "Super admin",
  admin: "Admin",
  facilitator: "Facilitator",
  teacher: "Teacher",
};

// The date reads inline; the exact time is on hover, so the cell stays short
// in a narrow table column.
function day(iso: string | null): string {
  if (!iso) return "";
  return formatDate(iso);
}

function exactTime(iso: string | null): string | undefined {
  return iso ? formatDateTime(iso) : undefined;
}

export function ActorName({ actor }: { actor: ActorRef }) {
  return (
    <span>
      <strong className="font-semibold text-content-primary">{actor.name}</strong>
      <span className="text-content-secondary"> ({roleLabels[actor.role] ?? actor.role})</span>
      {!actor.active && <span className="text-danger"> · deactivated</span>}
    </span>
  );
}

// "Edited by" and "Created by" for one record. A record with nobody on file
// says so, rather than showing nothing, so a gap in tracking is visible.
export function AttributionCell({ info }: { info: Attribution | undefined }) {
  if (!info) return <span className="text-content-disabled">…</span>;
  if (!info.created_by && !info.updated_by) {
    return <span className="text-xs text-content-secondary">Unknown (before tracking)</span>;
  }
  const sameEditor = info.updated_by?.id === info.created_by?.id;
  return (
    <div className="grid gap-0.5 text-xs leading-5">
      {info.updated_by && (
        <span>
          <span className="text-content-secondary">Edited by </span>
          <ActorName actor={info.updated_by} />
          {info.updated_at && (
            <span className="text-content-secondary" title={exactTime(info.updated_at)}>
              {" "}
              · {day(info.updated_at)}
            </span>
          )}
        </span>
      )}
      {info.created_by && !(sameEditor && info.created_at === info.updated_at) && (
        <span>
          <span className="text-content-secondary">Created by </span>
          <ActorName actor={info.created_by} />
          {info.created_at && (
            <span className="text-content-secondary" title={exactTime(info.created_at)}>
              {" "}
              · {day(info.created_at)}
            </span>
          )}
        </span>
      )}
    </div>
  );
}
